import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Modal } from 'flowbite-react';

import { DashboardTemplate } from 'modules/v2/templates';
import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { cancelModOrder, getModOrder } from 'modules/api/digitalServices/mailOnDemand';
import { PRINT_CREDITS_BALANCE_QUERY_KEY } from 'modules/v2/common/hooks/usePrintCreditsBalance';

import StatusChip from '../components/StatusChip';
import OrderStatusStepper from '../components/OrderStatusStepper';
import CopyableOrderId from '../components/CopyableOrderId';
import TrackPackageCard from '../components/TrackPackageCard';
import { CoverLetterChip } from '../components/CoverLetterChip';
import CoverLetterMiniPreview from '../components/CoverLetterMiniPreview';
import strings, { formatMessage } from '../strings';
import { CANCELLABLE_STATUSES, ORDER_STATUS } from '../constants';
import { formatCredits, formatDate } from '../utils';

export const MailOnDemandOrderDetailContent = ({ orderId }) => {
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(false);

  const { data: order, isLoading, isError, refetch } = useQuery(['modOrder', orderId], () =>
    getModOrder(orderId),
  );

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(false);

    try {
      const cancelled = await cancelModOrder(orderId);

      queryClient.setQueryData(['modOrder', orderId], cancelled);
      // A cancel after pay refunds the ledger — refresh the shared balance.
      queryClient.invalidateQueries(PRINT_CREDITS_BALANCE_QUERY_KEY);
      setShowCancelConfirm(false);
    } catch (error) {
      setCancelError(true);
    } finally {
      setCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
        <span className="text-neutral-500">{strings.common.loading}</span>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="flex flex-col items-start gap-3 py-6">
        <p>{strings.detail.loadError}</p>
        <Button color="outlined" onClick={() => refetch()}>
          {strings.common.retry}
        </Button>
      </div>
    );
  }

  const resolution = order.resolution || {};
  const pricing = order.pricing || {};
  const isCancellable = CANCELLABLE_STATUSES.includes(order.status);
  const isPaid = order.status === ORDER_STATUS.PAID;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <CopyableOrderId orderId={order._id} className="mb-1" />
          <div className="flex items-center gap-3">
            {order?.asset?.thumbnailUrl && (
              <img
                src={order.asset.thumbnailUrl}
                alt=""
                className="h-16 w-12 rounded border border-neutral-200 object-cover"
              />
            )}
            <div>
              <div className="text-lg font-semibold">{order.asset?.title}</div>
              <div className="text-xs text-neutral-400">
                {formatMessage(strings.orders.createdAt, { date: formatDate(order.createdAt) })}
              </div>
            </div>
          </div>
        </div>
        <StatusChip status={order.status} />
      </div>

      {order.status === ORDER_STATUS.CANCELLED && (
        <div
          className="mb-6 rounded-lg border border-neutral-300 bg-neutral-100 p-4 text-sm"
          data-testid="mod-cancelled-banner"
        >
          {strings.detail.cancelledBanner}
        </div>
      )}

      {order.status === ORDER_STATUS.RESOLUTION_FAILED && (
        <div
          className="mb-6 rounded-lg border border-error-500 bg-error-50 p-4 text-sm"
          data-testid="mod-failed-banner"
        >
          {strings.detail.failedBanner}
        </div>
      )}

      {order.status !== ORDER_STATUS.CANCELLED &&
        order.status !== ORDER_STATUS.RESOLUTION_FAILED && (
        <div className="mb-6 rounded-lg border border-neutral-200 bg-white p-4">
          <OrderStatusStepper status={order.status} />
        </div>
      )}

      <TrackPackageCard trackingRef={order.shipping?.trackingRef} />

      <div className="mb-6 max-w-xl rounded-lg border border-neutral-200 bg-white p-4">
        <h4 className="mb-2 text-sm font-semibold">{strings.detail.summaryTitle}</h4>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-neutral-100">
              <td className="py-2 text-neutral-500">{strings.review.mailableLabel}</td>
              <td className="py-2 text-right font-medium">
                {formatCredits(
                  resolution.selectedMailableCount ?? resolution.mailableCount,
                )}
              </td>
            </tr>
            <tr className="border-b border-neutral-100">
              <td className="py-2 text-neutral-500">{strings.review.skippedLabel}</td>
              <td className="py-2 text-right font-medium">
                {formatCredits(resolution.skippedCount)}
              </td>
            </tr>
            <tr>
              <td className="py-2 text-neutral-500">{strings.review.costLabel}</td>
              <td className="py-2 text-right font-medium">
                {formatCredits(pricing.creditsRequired)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {order.coverLetter && (
        <div
          className="mb-6 flex max-w-xl items-center gap-4 rounded-lg border border-primary-200 bg-primary-50 p-4"
          data-testid="mod-detail-cover-letter"
        >
          <div className="flex-none overflow-hidden rounded border border-neutral-200">
            <CoverLetterMiniPreview
              templateKey={order.coverLetter.templateKey}
              body={order.coverLetter.editedBody}
              width={64}
            />
          </div>
          <div className="min-w-0">
            <div className="mb-1">
              <CoverLetterChip templateKey={order.coverLetter.templateKey} />
            </div>
            <div className="text-sm font-semibold text-neutral-800">
              {strings.coverLetter.includedTitle}
            </div>
            <div className="text-xs text-neutral-500">{strings.coverLetter.includedBody}</div>
          </div>
        </div>
      )}

      {cancelError && <p className="mb-4 text-sm text-error-500">{strings.common.genericError}</p>}

      {isCancellable && (
        <Button
          color="dangerOutlined"
          onClick={() => setShowCancelConfirm(true)}
          data-testid="mod-cancel-order"
        >
          {strings.detail.cancelOrder}
        </Button>
      )}

      <Modal
        show={showCancelConfirm}
        size="md"
        popup
        onClose={() => setShowCancelConfirm(false)}
        className="font-figtree"
      >
        <Modal.Body className="pt-8 text-center">
          <h3 className="mb-2 text-lg font-semibold">{strings.detail.cancelConfirmTitle}</h3>
          <p className="mb-1 text-sm text-neutral-600">{strings.detail.cancelConfirmBody}</p>
          {isPaid && (
            <p className="text-sm text-neutral-600" data-testid="mod-refund-note">
              {formatMessage(strings.detail.cancelConfirmRefundNote, {
                creditsRequired: formatCredits(pricing.creditsRequired),
              })}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full justify-end gap-2">
            <Button
              color="outlined"
              onClick={() => setShowCancelConfirm(false)}
              disabled={cancelling}
            >
              {strings.detail.cancelConfirmNo}
            </Button>
            <Button
              color="danger"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="mod-cancel-confirm"
            >
              {strings.detail.cancelConfirmYes}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const MailOnDemandOrderDetailPage = () => {
  const { orderId } = useParams();

  return (
    <DashboardTemplate hasSidebar title={strings.detail.pageTitle}>
      <MailOnDemandOrderDetailContent orderId={orderId} />
    </DashboardTemplate>
  );
};

export default MailOnDemandOrderDetailPage;
